import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { post } from 'helpers/api_helper'
import EditorPhoto, { useEditorPhoto } from '../UserAccounts/EditorPhoto'

export const mediaIdentity = details => JSON.stringify([details.club?.id || details.club?.clubName || '', details.category])
// `slots` are the images shown for the draft's club and category. Previewing the same club and category again (`retry`) keeps them on
// screen, with any image the author picked, while the save waits for the new fingerprint (`ready`).
export function useEventMedia(data, draft) {
  const identity = mediaIdentity(draft)
  const changed = identity !== mediaIdentity(data.details)
  const [state, setState] = useState({})
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    let stale = false
    if (!changed) { setState({ identity, slots: data.media?.slots || {}, ready: true }); return undefined }
    const kept = current => current.identity === identity && current.slots ? { slots: current.slots } : {}
    setState(current => ({ identity, loading: true, ...kept(current) }))
    post('/admin/event-details/' + data.id + '/media-preview', { clubId: draft.club?.id || null, category: draft.category })
      .then(response => {
        if (!response.data?.fingerprint || !response.data?.slots) throw new Error('Invalid preview')
        if (!stale) setState({ ...response.data, identity, ready: true, confirmed: false })
      }).catch(() => { if (!stale) setState(current => ({ identity, error: 'Default images could not be loaded. Retry before saving.', ...kept(current) })) })
    return () => { stale = true }
  }, [identity, changed, data.id, data.media, draft.category, draft.club?.id, attempt])
  const retry = useCallback(() => setAttempt(value => value + 1), [])
  const current = state.identity === identity
  return { ...state, changed, ready: current && state.ready, slots: current ? state.slots : undefined,
    confirm: value => setState(previous => ({ ...previous, confirmed: value })), retry }
}
const ImageSlot = forwardRef(function ImageSlot({ slot, kind, eventId, locked, changed }, ref) {
  const photo = useEditorPhoto({ current: null, folder: 'events/' + eventId + '/drafts' })
  useImperativeHandle(ref, () => ({
    dirty: photo.dirty,
    upload: async () => {
      if (!photo.dirty) return undefined
      const filename = await photo.upload()
      return filename ? { filename, cleanupToken: photo.receipt(filename) } : null
    }, retain: photo.retain, release: photo.release, cleanup: photo.cleanup, invalidateUpload: photo.invalidateUpload,
  }))
  const preview = photo.dirty ? photo.preview : slot?.url ? (process.env.REACT_APP_API_URL || '') + slot.url : null
  return <div><EditorPhoto id={'event-image-' + kind} label={'Event ' + kind} locked={locked || !eventId}
    photo={{ ...photo, preview, select: file => { photo.select(file); changed() }, remove: () => { photo.remove(); changed() }, reset: () => { photo.reset(); changed() } }} />
    {!preview && <p>No image</p>}{preview && !photo.dirty && <small className="text-muted">{slot?.source === 'club' ? 'Club default' : 'Event image'}</small>}</div>
})
const EventEditMedia = forwardRef(function EventEditMedia({ media, error, eventId, locked, onDirty }, ref) {
  const slots = useRef({})
  useImperativeHandle(ref, () => ({
    upload: async () => {
      const result = {}
      for (const kind of ['logo', 'featured', 'cover']) {
        const upload = await slots.current[kind]?.upload()
        if (upload !== undefined) result[kind] = upload
      }
      return result
    },
    retain: () => Object.values(slots.current).forEach(slot => slot?.retain()),
    release: () => Object.values(slots.current).forEach(slot => slot?.release()),
    invalidateUpload: () => Object.values(slots.current).forEach(slot => slot?.invalidateUpload()),
    cleanup: () => Object.values(slots.current).forEach(slot => slot?.cleanup()),
  }))
  return <section><h3>Images</h3>
    {media.changed && <div className="alert alert-warning">Images were replaced automatically from the selected club and category defaults. Review all three images before saving.</div>}
    {media.loading && <p role="status">Loading default images…</p>}
    {media.error && <p role="alert">{media.error} <button type="button" className="btn btn-outline-primary" onClick={media.retry}>Retry images</button></p>}
    {media.slots && <div className="event-edit-grid">{['logo', 'featured', 'cover'].map(kind => <ImageSlot key={kind} kind={kind} eventId={eventId} slot={media.slots[kind]} locked={locked || !media.ready}
      ref={value => { if (value) slots.current[kind] = value }} changed={() => { media.confirm(false); onDirty(true) }} />)}</div>}
    {media.ready && media.changed && <label className="event-edit-check"><input type="checkbox" checked={Boolean(media.confirmed)} onChange={event => media.confirm(event.target.checked)} /> I have reviewed the updated images</label>}
    {error && <p id="event-edit-images" tabIndex={-1} role="alert" className="text-danger">{error}</p>}
  </section>
})
export default EventEditMedia
